import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  confirmAppEmailVerificationCode,
  exchangeFirebaseIdToken,
  fetchAuthMe,
  logoutRequest,
  sendAppEmailVerificationCode,
} from '../api/authApi';
import { firebaseApp } from '../firebase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('signin');

  const refreshSession = useCallback(async () => {
    try {
      const data = await fetchAuthMe();
      setUser(data.user ?? null);
    } catch {
      setUser(null);
    } finally {
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const openAuth = useCallback((mode = 'signin') => {
    setAuthModalMode(mode === 'signup' ? 'signup' : 'signin');
    setAuthModalOpen(true);
  }, []);

  const closeAuth = useCallback(() => {
    setAuthModalOpen(false);
  }, []);

  const sendVerificationCode = useCallback(async (opts = {}) => {
    if (!firebaseApp) {
      throw new Error('Firebase is not configured');
    }
    const { getAuth } = await import('firebase/auth');
    const u = getAuth(firebaseApp).currentUser;
    if (!u) {
      throw new Error('Your sign-in session expired. Try again.');
    }
    const idToken = await u.getIdToken();
    return sendAppEmailVerificationCode(idToken, opts);
  }, []);

  const confirmEmailVerification = useCallback(async (code) => {
    if (!firebaseApp) {
      throw new Error('Firebase is not configured');
    }
    const { getAuth } = await import('firebase/auth');
    const u = getAuth(firebaseApp).currentUser;
    if (!u) {
      throw new Error('Your sign-in session expired. Try again.');
    }
    const idToken = await u.getIdToken(true);
    const data = await confirmAppEmailVerificationCode(idToken, code);
    setUser(data.user ?? null);
    return data;
  }, []);

  const login = useCallback(async (email, password) => {
    if (!firebaseApp) {
      throw new Error('Firebase is not configured');
    }
    const { firebaseSignInWithEmail } = await import('./firebaseEmailAuth');
    const { idToken } = await firebaseSignInWithEmail(email, password);
    try {
      const data = await exchangeFirebaseIdToken(idToken);
      setUser(data.user ?? null);
      return { ...data };
    } catch (e) {
      if (e?.code === 'EMAIL_NOT_VERIFIED') {
        return { needsVerification: true };
      }
      throw e;
    }
  }, []);

  const signup = useCallback(async ({ username, email, password }) => {
    if (!firebaseApp) {
      throw new Error('Firebase is not configured');
    }
    const { firebaseSignUpWithEmail } = await import('./firebaseEmailAuth');
    const { idToken } = await firebaseSignUpWithEmail({ username, email, password });
    const sendRes = await sendAppEmailVerificationCode(idToken, { username });
    return { codeSent: true, emailed: sendRes.emailed === true };
  }, []);

  const signOutFirebaseOnly = useCallback(async () => {
    if (!firebaseApp || typeof window === 'undefined') {
      return;
    }
    try {
      const { getAuth, signOut } = await import('firebase/auth');
      await signOut(getAuth(firebaseApp));
    } catch {
      /* best-effort */
    }
  }, []);

  const loginWithGoogle = useCallback(async () => {
    const { getGoogleIdToken } = await import('./googleSignIn');
    const idToken = await getGoogleIdToken();
    const data = await exchangeFirebaseIdToken(idToken);
    setUser(data.user ?? null);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
      if (firebaseApp && typeof window !== 'undefined') {
        try {
          const { getAuth, signOut } = await import('firebase/auth');
          await signOut(getAuth(firebaseApp));
        } catch {
          /* Firebase session clear is best-effort */
        }
      }
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      authenticated: !!user,
      authChecked,
      authModalOpen,
      authModalMode,
      setAuthModalMode,
      openAuth,
      closeAuth,
      login,
      loginWithGoogle,
      signup,
      sendVerificationCode,
      confirmEmailVerification,
      signOutFirebaseOnly,
      logout,
      refreshSession,
    }),
    [
      user,
      authChecked,
      authModalOpen,
      authModalMode,
      openAuth,
      closeAuth,
      login,
      loginWithGoogle,
      signup,
      sendVerificationCode,
      confirmEmailVerification,
      signOutFirebaseOnly,
      logout,
      refreshSession,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
